import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { FaCheckCircle } from "react-icons/fa";
import { motion } from "framer-motion";

const ModalCarrito = ({ isOpen, product, quantity, price, relatedProducts = [], onClose }) => {
  const navigate = useNavigate();

  if (!isOpen || !product) return null;

  const getMainImage = (p) => {
    if (!p) return "/images/default-product.png";
    if (p.variants?.length > 0 && p.variants[0].colorImages?.length > 0)
      return p.variants[0].colorImages[0];
    return p.images?.[0] || "/images/default-product.png";
  };

  // Regla de oro: con variante-talla → precio de primera talla con stock.
  // Sin variante → precio directo con descuento si aplica.
  const getRelatedPrice = (p) => {
    if (!p) return 0;
    if (p.variants?.length > 0) {
      const sizes = p.variants[0]?.sizes || [];
      const size = sizes.find(s => s.stock > 0) || sizes[0];
      if (size) {
        return size.discount > 0 && size.discount < size.price
          ? size.discount
          : size.price || 0;
      }
    }
    return p.discount > 0 && p.discount < p.price ? p.discount : p.price || 0;
  };

  const handleIrAlCarrito = () => {
    onClose();
    navigate("/carrito");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative"
      >
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors z-10"
          aria-label="Cerrar"
        >
          <IoMdClose size={24} />
        </button>

        {/* Encabezado */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b">
          <FaCheckCircle className="text-green-500 text-2xl flex-shrink-0" />
          <h2 className="text-lg font-bold text-gray-800">
            Producto agregado a tu Carrito
          </h2>
        </div>

        {/* Detalle del producto */}
        <div className="flex items-center gap-4 px-6 py-5 border-b">
          <img
            src={getMainImage(product)}
            alt={product.name}
            className="w-20 h-20 object-contain border rounded flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm line-clamp-2">
              {product.name}
            </p>
            <p className="text-red-600 font-bold mt-1">
              S/ {price?.toFixed(2)}
            </p>
            <p className="text-gray-500 text-xs mt-1">Cantidad: {quantity}</p>
          </div>
        </div>

        {/* Cross-selling */}
        {relatedProducts.length > 0 && (
          <div className="px-6 py-4 border-b">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              ¿Y si le sumas lo último?
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {relatedProducts.slice(0, 5).map((rel, index) => {
                const img = getMainImage(rel);
                const relPrice = getRelatedPrice(rel);
                return (
                  <motion.div
                    key={rel._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.07, duration: 0.2 }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    className="min-w-[100px] max-w-[100px] flex-shrink-0"
                  >
                    <Link
                      to={`/product/details/${rel.slug}`}
                      onClick={onClose}
                      className="block text-center border rounded p-2 hover:border-red-400 hover:shadow-card-hover transition-all duration-200"
                    >
                      <div className="relative overflow-hidden rounded mb-1">
                        <img
                          src={img}
                          alt={rel.name}
                          className="w-14 h-14 object-contain mx-auto"
                        />
                      </div>
                      <p className="text-xs text-gray-600 truncate">{rel.name}</p>
                      <p className="text-xs font-bold text-red-600 mt-1">
                        S/ {relPrice.toFixed(2)}
                      </p>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 px-6 py-4">
          <button
            onClick={onClose}
            className="text-gray-600 font-semibold underline text-sm hover:text-gray-900 transition-colors"
          >
            Seguir comprando
          </button>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleIrAlCarrito}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-full font-bold text-sm transition-colors shadow-red-glow"
          >
            Ir al Carrito
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ModalCarrito;
